import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdCheckCircle, MdContentCopy, MdDeleteOutline, MdLanguage, MdRefresh } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getVideoCustomDomain,
    refreshVideoCustomDomain,
    removeVideoCustomDomain,
    setVideoCustomDomain,
    type VideoCustomDomain,
    type VideoCustomDomainStatus,
} from '@/lib/api/video/video-api'

const STATUS_LABELS: Record<VideoCustomDomainStatus, { label: string; className: string; hint: string }> = {
    PENDING_DNS: {
        label: 'Aguardando DNS',
        className: 'badge-warning',
        hint: 'Crie o registro CNAME abaixo no provedor do seu domínio. A verificação é automática.',
    },
    PENDING_CERT: {
        label: 'Emitindo certificado',
        className: 'badge-info',
        hint: 'O DNS está certo. O certificado de segurança costuma sair em alguns minutos.',
    },
    ACTIVE: {
        label: 'Ativo',
        className: 'badge-success',
        hint: 'Os links das salas já saem com o seu domínio.',
    },
    FAILED: {
        label: 'Falhou',
        className: 'badge-error',
        hint: 'Confira o registro CNAME, remova o domínio e cadastre de novo.',
    },
}

/** Enquanto espera DNS ou certificado, a tela relê o status sozinha. */
const POLL_INTERVAL_MS = 30_000

async function copyToClipboard(value: string) {
    try {
        await navigator.clipboard.writeText(value)
        toast.success('Copiado')
    } catch {
        toast.error('Não foi possível copiar')
    }
}

export default function VideoCustomDomainSection({ editable }: { editable: boolean }) {
    const [state, setState] = useState<VideoCustomDomain | null>(null)
    const [input, setInput] = useState('')
    const [busy, setBusy] = useState<'save' | 'refresh' | 'remove' | null>(null)
    const [confirmRemove, setConfirmRemove] = useState(false)

    useEffect(() => {
        void getVideoCustomDomain().then(response => {
            if (response.success && response.result) setState(response.result)
        })
    }, [])

    const pending = state?.status === 'PENDING_DNS' || state?.status === 'PENDING_CERT'
    useEffect(() => {
        if (!pending) return
        const timer = window.setInterval(() => {
            void getVideoCustomDomain().then(response => {
                if (response.success && response.result) setState(response.result)
            })
        }, POLL_INTERVAL_MS)
        return () => window.clearInterval(timer)
    }, [pending])

    if (!state) return null

    const handleSave = async () => {
        if (!input.trim()) {
            toast.error('Informe o domínio, ex.: meet.suaempresa.com.br')
            return
        }
        setBusy('save')
        const response = await setVideoCustomDomain(input.trim())
        setBusy(null)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível cadastrar o domínio')
            return
        }
        setState(response.result)
        setInput('')
        toast.success('Domínio cadastrado. Agora crie o registro no DNS.')
    }

    const handleRefresh = async () => {
        setBusy('refresh')
        const response = await refreshVideoCustomDomain()
        setBusy(null)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível verificar o domínio')
            return
        }
        setState(response.result)
        const next = response.result.status
        if (next === 'ACTIVE') toast.success('Domínio ativo')
        else if (next) toast.info(`Ainda não está pronto (${STATUS_LABELS[next].label})`)
    }

    const handleRemove = async () => {
        setBusy('remove')
        const response = await removeVideoCustomDomain()
        setBusy(null)
        setConfirmRemove(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível remover o domínio')
            return
        }
        setState(response.result)
        toast.success('Domínio removido. Os links voltaram ao endereço padrão.')
    }

    const status = state.status ? STATUS_LABELS[state.status] : null

    return (
        <section className="card bg-base-100 border border-base-200">
            <div className="card-body p-5 space-y-4">
                <div>
                    <h2 className="font-semibold">Domínio próprio</h2>
                    <p className="text-sm text-base-content/60">
                        A sala abre no endereço da sua empresa (ex.: meet.suaempresa.com.br) em vez do endereço
                        padrão. Os participantes não veem outro domínio.
                    </p>
                </div>

                {!state.domain && !state.availableInPlan && (
                    <div className="rounded-lg bg-base-200/60 px-4 py-3 text-sm text-base-content/70">
                        O seu plano não inclui domínio próprio. Fale com o suporte para contratar.
                    </div>
                )}

                {!state.domain && state.availableInPlan && !state.platformConfigured && (
                    <div className="rounded-lg bg-base-200/60 px-4 py-3 text-sm text-base-content/70">
                        O domínio próprio ainda não está disponível. Tente novamente mais tarde.
                    </div>
                )}

                {!state.domain && state.availableInPlan && state.platformConfigured && editable && (
                    <div className="form-control w-full">
                        <label className="label pb-1.5" htmlFor="video-custom-domain">
                            <span className="flex w-full items-baseline justify-between gap-3">
                                <span className="label-text font-medium">Endereço da sala</span>
                                <span className="label-text-alt text-base-content/50">use um subdomínio</span>
                            </span>
                        </label>
                        <div className="join w-full">
                            <input
                                id="video-custom-domain"
                                type="text"
                                inputMode="url"
                                autoCapitalize="off"
                                spellCheck={false}
                                className="input input-bordered join-item w-full"
                                value={input}
                                placeholder="meet.suaempresa.com.br"
                                onChange={event => setInput(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault()
                                        void handleSave()
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-primary join-item"
                                onClick={handleSave}
                                disabled={busy !== null}
                            >
                                {busy === 'save' ? <span className="loading loading-spinner loading-sm" /> : <MdLanguage />}
                                Cadastrar
                            </button>
                        </div>
                    </div>
                )}

                {state.domain && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-base-200/60 px-4 py-3">
                            <div className="min-w-0">
                                <div className="font-mono text-sm break-all">{state.domain}</div>
                                {status && <p className="text-xs text-base-content/60 mt-1">{status.hint}</p>}
                            </div>
                            {status && <span className={`badge ${status.className} shrink-0`}>{status.label}</span>}
                        </div>

                        {state.error && state.status !== 'ACTIVE' && (
                            <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm">
                                <span className="font-medium">Retorno do verificador: </span>
                                <span className="break-words">{state.error}</span>
                            </div>
                        )}

                        {state.status !== 'ACTIVE' && state.records.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium">Registros no DNS do seu domínio</h3>
                                <ul className="space-y-2">
                                    {state.records.map(record => (
                                        <li
                                            key={`${record.type}-${record.name}`}
                                            className="rounded-lg border border-base-200 p-3 space-y-2"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="badge badge-ghost font-mono">{record.type}</span>
                                                <span className="text-xs text-base-content/60">
                                                    {record.required ? 'Obrigatório' : 'Opcional, adianta a verificação'}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-[5rem_1fr_auto] items-center gap-x-3 gap-y-1 text-sm">
                                                <span className="text-base-content/60">Nome</span>
                                                <span className="font-mono break-all">{record.name}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs justify-self-start"
                                                    onClick={() => void copyToClipboard(record.name)}
                                                >
                                                    <MdContentCopy /> Copiar
                                                </button>
                                                <span className="text-base-content/60">Valor</span>
                                                <span className="font-mono break-all">{record.value}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs justify-self-start"
                                                    onClick={() => void copyToClipboard(record.value)}
                                                >
                                                    <MdContentCopy /> Copiar
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-xs text-base-content/60">
                                    Se o seu DNS estiver no Cloudflare, deixe o CNAME com a nuvem cinza (somente DNS).
                                    A propagação pode levar de minutos a algumas horas.
                                </p>
                            </div>
                        )}

                        {state.status === 'ACTIVE' && (
                            <p className="flex items-center gap-2 text-sm text-success">
                                <MdCheckCircle className="shrink-0" />
                                Salas em https://{state.domain}/r/...
                            </p>
                        )}

                        <div className="flex flex-wrap justify-end gap-2">
                            {state.status !== 'ACTIVE' && (
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={handleRefresh}
                                    disabled={busy !== null}
                                >
                                    {busy === 'refresh' ? <span className="loading loading-spinner loading-xs" /> : <MdRefresh />}
                                    Verificar agora
                                </button>
                            )}
                            {editable && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-ghost text-error"
                                    onClick={() => setConfirmRemove(true)}
                                    disabled={busy !== null}
                                >
                                    <MdDeleteOutline />
                                    Remover domínio
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={confirmRemove}
                onClose={() => setConfirmRemove(false)}
                onConfirm={handleRemove}
                loading={busy === 'remove'}
                title="Remover domínio próprio"
                message={`Os links das salas voltam ao endereço padrão. Links já enviados com ${state.domain ?? 'o domínio'} deixam de abrir.`}
                confirmButtonText="Remover"
            />
        </section>
    )
}
